/**
 * BabaWorkPanel — OS Dashboard widget
 * Baba Filiz'in son işleri, Anne onay durumu, expert atamaları
 */
import axios from 'axios'
import {
  Bot, CheckCircle2, Clock, Crown, Loader2, Plus, Shield,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../../config/api'

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` })

interface BabaSummary {
  total: number
  pending: number
  processing: number
  completed: number
  anne_approved: number
  recent: Array<{
    id: number
    title: string
    category: string
    subcategory: string
    status: string
    flz_cost: number
    expert_agent_id?: number
    anne_eq_score?: number
    anne_approved?: number
  }>
}

const statusIcon = (s: string) => {
  if (s === 'completed') {return <CheckCircle2 size={10} color="#10b981" />}
  if (s === 'processing') {return <Loader2 size={10} color="#6366f1" style={{ animation: 'spin 1.5s linear infinite' }} />}
  return <Clock size={10} color="#f59e0b" />
}

const catColor: Record<string, string> = {
  audio_processing: '#3b82f6',
  image_generation: '#ec4899',
  content_writing: '#a855f7',
  code: '#10b981',
  data_analysis: '#f59e0b',
  social_automation: '#06b6d4',
}

export default function BabaWorkPanel() {
  const [data, setData] = useState<BabaSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API_URL}/api/baba/work/summary`, { headers: authHdr() })
      .then(r => setData(r.data as BabaSummary))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div style={{
      background: 'var(--os-win-bg)',
      border: '1px solid var(--os-border)',
      borderRadius: 14, padding: '14px 16px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{
            width: 24, height: 24, borderRadius: 7,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(99,102,241,0.3)',
          }}>
            <Crown size={12} color="#fff" />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>
            Baba Filiz Work
          </span>
        </div>
        <Link to="/dashboard/work" style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 10, color: '#6366f1', textDecoration: 'none',
        }}>
          <Plus size={10} /> New Task
        </Link>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 11, color: 'var(--os-muted)' }}>
          Loading...
        </div>
      ) : !data || data.total === 0 ? (
        <div style={{ textAlign: 'center', padding: '14px 0', fontSize: 11, color: 'var(--os-muted)' }}>
          No tasks yet. Baba Filiz is ready to work!
        </div>
      ) : (
        <>
          {/* Summary counters */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[
              { label: 'Pending', value: data.pending, color: '#f59e0b' },
              { label: 'Running', value: data.processing, color: '#6366f1' },
              { label: 'Done', value: data.completed, color: '#10b981' },
              { label: 'Anne ✓', value: data.anne_approved, color: '#ec4899' },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, textAlign: 'center', padding: '6px 4px',
                borderRadius: 8, background: `${s.color}10`,
                border: `1px solid ${s.color}20`,
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 9, color: 'var(--os-muted)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Recent tasks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.recent.map(t => (
              <Link key={t.id} to={`/work/${t.id}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', borderRadius: 8,
                background: 'var(--os-surface)',
                border: '1px solid var(--os-border)',
                textDecoration: 'none', transition: 'border-color 0.15s',
              }}>
                {/* Category dot */}
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: catColor[t.category] ?? '#6b7280',
                }} />
                {/* Title */}
                <div style={{
                  flex: 1, fontSize: 11, color: 'var(--os-text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {t.title}
                </div>
                {/* Expert */}
                {t.expert_agent_id && (
                  <Bot size={10} color={`hsl(${(t.expert_agent_id * 47) % 360},65%,48%)`} />
                )}
                {/* Anne status */}
                {t.anne_approved ? (
                  <Shield size={9} color="#10b981" />
                ) : t.anne_eq_score !== null ? (
                  <Shield size={9} color="#f59e0b" />
                ) : null}
                {/* Status */}
                {statusIcon(t.status)}
                {/* FLZ */}
                <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600, flexShrink: 0 }}>
                  {t.flz_cost.toFixed(1)}
                </span>
              </Link>
            ))}
          </div>
        </>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
